<template>
  <template v-if="maxSmall">
    <MobileHeader
      :key="type"
      :organisation="organisationName"
      :buildings="buildings"
      :type="type"
      :views="views"
      :currentView="currentView"
      :hasUnread="unreadCount > 0"
      @openNotifications="openNotifications = true"
      @openFilter="openFilters = true"
      @openViewSelector="openViewSelector = true"
      :isSettingsOverview="isSettingsOverview"
      @go-back="goBack"
    />
    <slot />
  </template>
  <Nav
    :user="user"
    :organisation="organisationName"
    :buildings="buildings"
    :primary="primary"
    :secondary="secondary"
    :isSettingsOverview="isSettingsOverview"
    @openNotifications="openNotifications = true"
    @openProfile="openProfile = true"
    @go-back="goBack"
  />
  <template v-if="!maxSmall">
    <slot />
  </template>
  <ViewSelectorDrawer
    :key="type"
    :open="openViewSelector"
    @update:open="openViewSelector = $event"
    :type="type"
    :views="views"
    :currentView="currentView"
    @addView="handleAddView"
    @renameView="handleRenameView"
    @deleteView="handleDeleteView"
    @selectView="handleSelectView"
  />
  <FiltersDrawer
    :key="buildingId + type"
    :open="openFilters"
    @update:open="openFilters = $event"
    :type="type"
    :views="views"
    :currentView="currentView"
    :buildingId="buildingId"
    @addView="handleAddView"
    @updateView="handleUpdateView"
  />
  <Notifications
    v-model:open="openNotifications"
    :buildings="buildings"
    :unreadCount="unreadCount"
  />
  <Profile v-model:open="openProfile" :buildings="buildings" />
</template>

<script setup>
import route from "ziggy-js";
import { computed, inject, ref, watch, watchEffect } from "vue";
import { isEmpty } from "lodash";
import { usePage, router } from "@inertiajs/vue3";
import { MobileHeader, Nav } from "@/SharedComponents/Navigation";
import { Drawer as FiltersDrawer } from "@/SharedComponents/Filters/Drawer";
import { Notifications } from "@/Pages/Notifications";
import { Profile } from "@/Pages/Profile";
import { useSites } from "@/Composables/useSites";
import { useUnreadCount } from "@/Composables/useNotifications";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";
import { useFilters } from "@/Composables/useFilters";
import { storeToRefs } from "pinia";
import { ViewSelectorDrawer } from "@/SharedComponents/Navigation/ViewSelectorDrawer";
import { useRoutes } from "@/Composables/useRoutes";

import { useDefaultTasksViews } from "@/Pages/Tasks/Views";
import { useTasksViews } from "@/Composables/useTasksViews";

import { useDefaultFilesViews } from "@/Pages/Files/Views";
import { useFilesViews } from "@/Composables/useFilesViews";

import { useDefaultAssetsViews } from "@/Pages/Assets/Views";
import { useAssetsViews } from "@/Composables/useAssetsViews";

import { useDefaultSpacesViews } from "@/Pages/Spaces/Views";
import { useDefaultRecurringTasksViews } from "@/Pages/RecurringTasks/Views";

const { hasPermission, permissions } = inject("permissions");
const { updateRoute, goBack } = useRoutes();

const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

const buildingId = computed(() => globalFilterValues.value.tenant?.[0] || "");

const { maxSmall, maxTablet } = useBreakpoints();
const { t } = useI18n();

const openNotifications = ref(undefined);
const openFilters = ref(undefined);
const openViewSelector = ref(undefined);
const openProfile = ref(undefined);
const { data: unreadCount } = useUnreadCount();

const url = computed(() => usePage().url);
const isSettingsOverview = computed(() => url.value.includes("/settings"));
const user = computed(() => usePage().props.auth?.user);
const organisationName = computed(
  () => usePage().props.tenant?.customer?.company_name
);
const { data: buildings } = useSites();
const helpCenterLink = computed(() => {
  switch (usePage().props.locale) {
    case "nl":
      return "https://ondersteuning.fixform.com";
    case "fr":
      return "https://soutien.fixform.com";
    default:
      return "https://support.fixform.com";
  }
});

const type = computed(() => {
  if (url.value.includes("/admin/files")) {
    return "files";
  }

  if (url.value.includes("/admin/insights")) {
    return "insights";
  }

  if (url.value.includes("/admin/assets")) {
    return "assets";
  }

  if (url.value.includes("/admin/asset-groups")) {
    return "asset_groups";
  }

  if (url.value.includes("/admin/spaces")) {
    return "spaces";
  }

  if (url.value.includes("admin/settings/organisation/recurring-tasks")) {
    return "recurring_tasks";
  }

  if (url.value.includes("/admin/settings")) {
    return "organisation";
  }

  return "tasks";
});

const tasks = computed(() => ({
  id: "tasks",
  icon: "film-check",
  title: t("admin.navigation.tasks.label"),
  href: route("tasks.index"),
  active: url.value.includes("/admin/tasks"),
}));

const files = computed(() => {
  if (
    !buildingId.value &&
    buildings.value?.every(
      (building) => !hasPermission(permissions.readDocument, building.id)
    )
  ) {
    return null;
  }
  if (
    buildingId.value &&
    !hasPermission(permissions.readDocument, buildingId.value)
  ) {
    return null;
  }
  return {
    id: "files",
    icon: "documents",
    title: t("admin.navigation.files.label"),
    href: route("files.index"),
    active: url.value.includes("/admin/files"),
  };
});

const assets = computed(() => {
  if (
    !buildingId.value &&
    buildings.value?.every(
      (building) => !hasPermission(permissions.readAsset, building.id)
    )
  ) {
    return null;
  }
  if (
    buildingId.value &&
    !hasPermission(permissions.readAsset, buildingId.value)
  ) {
    return null;
  }
  return {
    id: "assets",
    icon: "asset",
    title: t("admin.navigation.assets.label"),
    href: route("assets.index"),
    active:
      url.value.includes("/admin/assets") ||
      url.value.includes("/admin/asset-groups"),
  };
});

const spaces = computed(() => {
  if (
    !buildingId.value &&
    buildings.value?.every(
      (building) => !hasPermission(permissions.readSpace, building.id)
    )
  ) {
    return null;
  }

  if (
    buildingId.value &&
    !hasPermission(permissions.readSpace, buildingId.value)
  ) {
    return null;
  }
  return {
    id: "spaces",
    icon: "location-marker",
    title: t("admin.navigation.spaces.label"),
    href: route("spaces.index"),
    active: url.value.includes("/admin/spaces"),
  };
});

const insights = computed(() => ({
  id: "insights",
  icon: "insights",
  title: t("admin.navigation.insights.label"),
  href: route(
    "insights",
    buildingId.value !== "" ? [buildingId.value] : usePage().props.tenant
  ),
  active: url.value.includes("/admin/insights"),
  hidden: hasPermission(permissions.limitedAccess),
}));

const marketplace = computed(() => ({
  id: "marketplace",
  icon: "shopping-basket",
  title: t("admin.navigation.marketplace.label"),
  href: "https://fixform.myshopify.com/en",
  external: true,
  trackingId: "nav.marketplace",
}));

const notifications = computed(() => ({
  id: "notifications",
  icon: "bell-default",
  title: t("admin.navigation.notifications.label"),
  count: unreadCount.value,
  modal: true,
  trackingId: "nav.notifications",
}));

const organisation = computed(() => ({
  id: "organisation",
  icon: "settings-cog",
  title: t("admin.navigation.settings.label"),
  href: maxTablet.value
    ? route("settings.organisation.index")
    : route("settings.organisation.buildings"),
  active:
    url.value.includes("/admin/settings") &&
    !url.value.includes("/admin/settings/personal"),
}));

const help = computed(() => ({
  id: "help",
  icon: "message-question",
  title: t("admin.navigation.help.label"),
  href: helpCenterLink.value,
  external: true,
  trackingId: "nav.help-support",
}));

const buildingsPage = computed(() => ({
  id: "buildings",
  icon: "building-group",
  title: t("admin.navigation.buildings.label"),
  href: route("settings.organisation.buildings"),
  active: url.value.includes("/settings/organisation/buildings"),
}));

const recurringTasksPage = computed(() => {
  if (
    buildings.value?.some((building) =>
      hasPermission(permissions.readRecurringTask, building.id)
    )
  ) {
    return {
      id: "recurring-tasks",
      icon: "repeat",
      title: t("admin.navigation.recurring_tasks.label"),
      href: route("settings.organisation.recurring-tasks.index"),
      active: url.value.includes(
        "/admin/settings/organisation/recurring-tasks"
      ),
    };
  }

  return null;
});

const menu = computed(() => ({
  id: "menu",
  icon: "menu-hamburger",
  label: maxSmall.value ? t("admin.navigation.groups.more") : undefined,
  items: maxSmall.value
    ? [
        spaces.value,
        insights.value,
        organisation.value,
        help.value,
        marketplace.value,
      ].map((item) => ({
        ...item,
        viewport: "desktop",
      }))
    : [
        tasks.value,
        files.value,
        assets.value,
        spaces.value,
        insights.value,
      ].filter((item) => item !== null),
  active: maxSmall.value
    ? insights.value.active || organisation.value.active
    : false,
}));

const settingsMenu = computed(() => ({
  id: "menu",
  icon: "menu-hamburger",
  label: maxSmall.value ? t("admin.navigation.groups.more") : undefined,
  items: maxSmall.value
    ? [insights.value, marketplace.value, organisation.value, help.value].map(
        (item) => ({
          ...item,
          viewport: "desktop",
        })
      )
    : [buildingsPage.value, recurringTasksPage.value].filter(
        (item) => item !== null
      ),
  active: maxSmall.value
    ? insights.value.active || organisation.value.active
    : false,
}));

const primary = computed(() => {
  if (isSettingsOverview.value) {
    if (maxSmall.value) {
      return {
        items: [tasks.value, files.value, assets.value].filter(
          (item) => item !== null
        ),
      };
    }

    return [settingsMenu.value];
  } else {
    if (maxSmall.value) {
      return {
        items: [tasks.value, files.value, assets.value].filter(
          (item) => item !== null
        ),
      };
    }
    return [menu.value];
  }
});

const secondary = computed(() => {
  if (isSettingsOverview.value) {
    if (maxSmall.value) {
      return settingsMenu.value;
    }
    return [];
  }
  if (maxSmall.value) {
    return menu.value;
  }
  return {
    items: [
      notifications.value,
      organisation.value,
      help.value,
      marketplace.value,
    ],
  };
});

const { customViews: customAssetsViews, isLoading: isAssetsViewsLoading } =
  useAssetsViews();
const { defaultViews: defaultAssetsViews, DefaultView: DefaultAssetsView } =
  useDefaultAssetsViews();

const { customViews: customFilesViews, isLoading: isFilesViewsLoading } =
  useFilesViews();
const { defaultViews: defaultFilesViews, DefaultView: DefaultFilesView } =
  useDefaultFilesViews();

const { customViews: customTasksViews, isLoading: isTasksViewsLoading } =
  useTasksViews();
const { defaultViews: defaultTasksViews, DefaultView: DefaultTasksView } =
  useDefaultTasksViews();
const { defaultViews: defaultSpacesViews, DefaultView: DefaultSpacesView } =
  useDefaultSpacesViews();
const {
  defaultViews: defaultRecurringTasksViews,
  DefaultView: DefaultRecurringTasksView,
} = useDefaultRecurringTasksViews();

const filterStore = useFilters(type);
const { handleInitialiseFilters, handleSwitchView } = filterStore;

const views = ref([]);

const getActiveDefaultView = () => {
  switch (type.value) {
    case "files":
      return DefaultFilesView.ALL;
    case "assets":
      return DefaultAssetsView.ALL;
    case "asset_groups":
      return DefaultAssetsView.ASSET_GROUPS;
    case "spaces":
      return DefaultSpacesView.ALL;
    case "recurring_tasks":
      return DefaultRecurringTasksView.ALL;
    default:
      return DefaultTasksView.ALL;
  }
};

const activeView = ref(getActiveDefaultView());

watchEffect(() => {
  if (!isFilesViewsLoading.value && type.value === "files") {
    views.value = defaultFilesViews;

    if (!isEmpty(customFilesViews.value)) {
      views.value = views.value.concat(customFilesViews.value);
    }
  }

  if (!isTasksViewsLoading.value && type.value === "tasks") {
    views.value = defaultTasksViews;

    if (!isEmpty(customTasksViews.value)) {
      views.value = views.value.concat(customTasksViews.value);
    }
  }

  if (type.value === "spaces") {
    views.value = defaultSpacesViews;
  }

  if (type.value === "recurring_tasks") {
    views.value = defaultRecurringTasksViews;
  }

  if (
    !isAssetsViewsLoading.value &&
    (type.value === "assets" || type.value === "asset_groups")
  ) {
    const hasSelectedBuilding = globalFilterValues.value.tenant;
    views.value = defaultAssetsViews.value.map((view) => {
      if (
        !hasSelectedBuilding &&
        view.id === DefaultAssetsView.ASSET_GROUPS.id
      ) {
        return {
          ...view,
          disabled: true,
        };
      }

      return view;
    });

    if (!isEmpty(customAssetsViews.value)) {
      views.value = views.value.concat(customAssetsViews.value);
    }
  }
});

watch(type, () => {
  activeView.value = getActiveDefaultView();
});

watch(
  buildingId,
  () => {
    handleSelectView(getActiveDefaultView());
  },
  {
    deep: true,
  }
);

const currentView = computed(() => {
  if (activeView.value.default) {
    return {
      ...activeView.value,
      name: activeView.value.name,
    };
  }

  return activeView.value;
});
const handleDeleteView = (viewId) => {
  views.value = views.value.filter((item) => item.id !== viewId);
};

const handleAddView = (view) => {
  view.default = false;
  views.value.push(view);
  handleSelectView(view);
};

const handleUpdateView = (view) => {
  activeView.value = view;
  views.value[views.value.findIndex((item) => item.id === view.id)] = view;
};

const handleRenameView = (view) => {
  views.value[views.value.findIndex((item) => item.id === view.id)] = view;

  if (activeView.value.id === view.id) {
    activeView.value = view;
  }
};

const handleSelectView = (view) => {
  if (view.href) {
    activeView.value = view;
    openViewSelector.value = false;
    router.visit(view.href, {
      preserveScroll: true,
      preserveState: true,
    });
  } else {
    handleSwitchView(view);
    activeView.value = view;
    openViewSelector.value = false;

    const filters = view.filters || view.criteria;

    if (!view.default) {
      handleInitialiseFilters(filters);
      return;
    }

    const defaultFilters = filters.getActiveFilterKeys();

    handleInitialiseFilters(filters, defaultFilters);
  }
};

router.on("navigate", (event) => {
  updateRoute(event.detail.page.url);
});
</script>
